import { useEffect, useState } from "react";
import useWindowSize from "../../../lib/hooks/useWindowSize";
import {
  FiFacebook,
  FiMail,
  FiLinkedin,
  FiTwitter,
  FiShare2,
  FiLink,
} from "react-icons/fi";

import styles from "./SocialShare.module.scss";
import CopyUrl from "./CopyUrl";
import Button from "@/components/ui/buttons/Button/Button";
import { AiOutlineArrowLeft, AiOutlineArrowRight } from "react-icons/ai";

export default function SocialShare({
  blog,
  config,
  url,
  direction,
  styled,
  mode = "manual",
  style = "default",
  copyUrl = false,
  ctaTitle,
  ctaLink,
}) {
  const [canUseShareApi, setCanUseShareApi] = useState(false);
  const page = blog;

  const windowSize = useWindowSize();

  useEffect(() => {
    if (
      typeof navigator?.canShare === "function" &&
      navigator?.canShare({ title: "test", url: "test", text: "test" })
    ) {
      setCanUseShareApi(true);
    }
  }, []);

  return (
    <div
      className={`${styles.socialShareContainer} ${
        ctaLink ? styles.multiButton : ""
      }`}
    >
      {mode === "shareApi" && canUseShareApi === true && (
        <ShareApiSocialShare page={page} config={config} />
      )}
      {mode === "manual" && (
        <ManualSocialShare
          page={page}
          config={config}
          url={url}
          direction={direction}
          styled={styled}
          style={style}
          windowSize={windowSize}
          canUseShareApi={canUseShareApi}
          copyUrl={copyUrl}
        />
      )}
      {ctaTitle && ctaLink && (
        <CtaButton ctaTitle={ctaTitle} ctaLink={ctaLink} />
      )}
    </div>
  );
}

function CtaButton({
  ctaTitle,
  ctaLink,
  icon = <AiOutlineArrowRight size={20} />,
}) {
  return (
    <Button link={ctaLink} classes={[styles.ctaButton]} fill="light">
      <span className={`${styles.title}`}>{ctaTitle}</span>{" "}
      <span className={`${styles.icon}`}>{icon}</span>
    </Button>
  );
}

function ManualSocialShare({
  page,
  config = { facebook: {}, linkedin: {}, twitter: {}, email: {} },
  url,
  direction,
  styled,
  style,
  windowSize,
  canUseShareApi,
  copyUrl,
}) {
  if (style === "default") {
    return (
      <DefaultManualShare
        url={url}
        direction={direction}
        styled={styled}
        config={config}
        page={page}
        windowSize={windowSize}
        copyUrl={copyUrl}
      />
    );
  }

  if (style === "dropdown") {
    return (
      <DropdownSocialShare
        url={url}
        config={config}
        page={page}
        windowSize={windowSize}
        canUseShareApi={canUseShareApi}
        copyUrl={copyUrl}
      />
    );
  }
}

function DefaultManualShare({ config, url, direction, styled, page, copyUrl }) {
  const iconConfig = {
    size: 25,
    color: "var(--dark-text)",
  };

  return (
    <div
      className={`${styles.container} ${direction && styles[direction]} ${
        styled ? styles.styled : ""
      }`}
    >
      <div className={`${styles.shareIcon}`}>
        <FiShare2 size={25} color="var(--light-text)" />
      </div>

      <ShareLinks
        iconConfig={iconConfig}
        url={url}
        config={config}
        page={page}
        direction={direction}
        copyUrl={copyUrl}
        key={copyUrl}
      />
    </div>
  );
}

function DropdownSocialShare({
  config,
  url,
  page,
  windowSize,
  canUseShareApi,
  copyUrl,
}) {
  const [isDropdownActive, setIsDropdownActive] = useState(false);

  const handleShareApi = async () => {
    try {
      if (
        navigator.canShare({
          title: config?.title,
          text: config?.text,
          url: config?.url,
        })
      ) {
        await navigator.share({
          title: config?.title,
          text: config?.text,
          url: config?.url,
        });
      } else {
        throw new Error("Web Share API not supported");
      }
    } catch (error) {
      console.error("Error sharing:", error.message);
    }
  };

  const handleShareInteraction = () => {
    if (windowSize?.width <= 768 && canUseShareApi) {
      handleShareApi();
      return;
    }

    setIsDropdownActive(!isDropdownActive);
  };

  const iconConfig = {
    size: 25,
    color: "var(--dark-text)",
  };

  return (
    <div className={`${styles.dropdownContainer}`}>
      <button
        className={`${styles.dropdownToggleButton}`}
        onClick={handleShareInteraction}
        onKeyDown={(event) => {
          event.preventDefault();
          event.key === "Enter" ? handleShareInteraction() : "";
        }}
        tabIndex="1"
        type="button"
      >
        Share <FiShare2 size={25} />
      </button>
      {(windowSize?.width >= 768 || !canUseShareApi) && (
        <div
          className={`${styles.dropdownContent} ${
            isDropdownActive ? styles.dropdownActive : styles.dropdownInactive
          }`}
        >
          <ShareLinks
            url={url}
            config={config}
            page={page}
            iconConfig={iconConfig}
            copyUrl={copyUrl}
            key={copyUrl}
          />
        </div>
      )}
    </div>
  );
}

function ShareApiSocialShare({ config }) {
  const { title, text, url } = config;

  const handleShare = async () => {
    try {
      if (navigator.canShare({ title, text, url })) {
        await navigator.share({
          title,
          text,
          url,
        });
      } else {
        throw new Error("Web Share API not supported");
      }
    } catch (error) {
      console.error("Error sharing:", error.message);
    }
  };

  const handleClick = () => {
    handleShare();
  };

  const handleKeyDown = (event) => {
    // Trigger share on Enter key press
    if (event.key === "Enter") {
      handleShare();
    }
  };

  return (
    <button
      className={`${styles.shareButton}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      type="button"
    >
      <FiShare2 size={25} />
    </button>
  );
}

function ShareLinks({ iconConfig, url, config, page, direction, copyUrl }) {
  const [copyUrlRender, setCopyUrlRender] = useState(copyUrl);

  useEffect(() => {
    setTimeout(() => {
      setCopyUrlRender(!copyUrl);
    }, 1);
  }, [copyUrl, url]);

  return (
    <div className={`${styles.links} ${direction && styles[direction]}`}>
      <a
        target="_blank"
        rel="noopener noreferrer"
        className={`${styles.link} social-share-link`}
        href={`https://www.facebook.com/sharer.php?u=${url}`}
      >
        <FiFacebook {...iconConfig} />
      </a>
      <a
        target="_blank"
        rel="noopener noreferrer"
        className={`${styles.link}`}
        href={`https://www.linkedin.com/shareArticle?mini=true&url=${url}`}
      >
        <FiLinkedin {...iconConfig} />
      </a>
      <a
        target="_blank"
        rel="noopener noreferrer"
        className={`${styles.link}`}
        href={`https://twitter.com/intent/tweet?url=${url}${
          config?.text ? `&text=${config?.text}` : ""
        }`}
      >
        <FiTwitter {...iconConfig} />
      </a>
      <a
        target="_blank"
        rel="noopener noreferrer"
        className={`${styles.link}`}
        href={`mailto:?&subject=${page?.title ?? config?.title}&cc=&bcc=&body=${
          page?.description ?? config?.text
        } 
        %0d%0a %0d%0a 
        Check it out: ${url ?? ""} `}
        // href={`mailto:?&subject=${page?.title}&cc=&bcc=&body=${url} %0d%0a %0d%0a ${blog?.description}`}
      >
        <FiMail {...iconConfig} />
      </a>
      {!!copyUrlRender && !!url && <CopyUrl iconConfig={iconConfig} />}
    </div>
  );
}
